// Typed wrappers over the /api/admin/* surface. Each function maps to one
// backend route and returns the wire shape verbatim (the response IS the
// contract — adding a transform layer here would just be another place
// for drift to hide).

import { apiCall } from './client';

export interface AdminMe {
  email: string;
  is_admin: boolean;
}

export function getAdminMe(signal?: AbortSignal): Promise<AdminMe> {
  return apiCall<AdminMe>('/api/admin/me', { signal });
}

// ---- menu_imports list ----------------------------------------------------

export type MenuImportStatus = 'draft' | 'approved' | 'rejected' | 'failed';
export type QualityDecision = 'publish' | 'hold' | 'reject';
export type MenuImportSort = 'recent' | 'priority';
export type SourceKind = 'local_path' | 'http_url';
export type SourceDocKind =
  | 'visual_menu'
  | 'nutrition_doc'
  | 'allergen_chart'
  | 'beverage_list';

// Mirrors the backend's `MenuImportSummary` shape verbatim. If the backend
// ever drops or renames a field here, the TS compiler will tell us via the
// list rendering downstream — much faster than a 500 in prod.
export interface MenuImportSummary {
  id: number;
  restaurantId: number;
  restaurantSlug: string;
  restaurantName: string;
  sourceKind: SourceKind;
  sourceDocKind: SourceDocKind;
  sourcePathOrUrl: string;
  pipelineVersion: string;
  extractorUsed: string;
  structurerUsed: string;
  totalCostUsd: number;
  totalDurationMs: number;
  itemsCount: number;
  status: MenuImportStatus;
  errorMessage: string | null;
  extractedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  // B3 draft-quality verdict. null until backfilled / for failed imports.
  qualityScore: number | null;
  qualityDecision: QualityDecision | null;
  qualityFlags: string[];
  reuseTelemetry: ImportReuseTelemetry | null;
}

export interface ImportReuseTelemetry {
  exact: number;
  aliased: number;
  created: number;
  trivial: number;
  unresolved: number;
  attributeConflicts: number;
  attributeAware: number;
  reuseRatio: number | null;
}

export interface ListMenuImportsResponse {
  rows: MenuImportSummary[];
  total: number;
}

export interface ListMenuImportsParams {
  status?: MenuImportStatus;
  restaurant_id?: number;
  source_doc_kind?: SourceDocKind;
  pipeline_version?: string;
  sort?: MenuImportSort;
  limit?: number;
  offset?: number;
}

export function listMenuImports(
  params: ListMenuImportsParams,
  signal?: AbortSignal,
): Promise<ListMenuImportsResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.restaurant_id !== undefined) {
    search.set('restaurant_id', String(params.restaurant_id));
  }
  if (params.source_doc_kind) search.set('source_doc_kind', params.source_doc_kind);
  if (params.pipeline_version) search.set('pipeline_version', params.pipeline_version);
  if (params.sort) search.set('sort', params.sort);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const qs = search.toString();
  const path = qs ? `/api/admin/menu-imports?${qs}` : '/api/admin/menu-imports';
  return apiCall<ListMenuImportsResponse>(path, { signal });
}

// ---- menu_imports detail --------------------------------------------------

// The structured MenuSection / MenuItem shape the extractor + the
// approve/edit flows consume. Field set mirrors backend/src/menu/extract/types.ts.
export interface ExtractedMenuItem {
  title: string;
  description?: string;
  ingredients: string[];
  portionGrams?: number;
  allergens: string[];
}

export interface ExtractedMenuSection {
  title?: string;
  items: ExtractedMenuItem[];
}

export interface MenuImportDetail {
  id: number;
  restaurantId: number;
  restaurantSlug: string;
  restaurantName: string;
  sourceKind: SourceKind;
  sourceDocKind: SourceDocKind;
  sourcePathOrUrl: string;
  sourceHash: string;
  pipelineVersion: string;
  extractorUsed: string;
  structurerUsed: string;
  sections: ExtractedMenuSection[];
  totalCostUsd: number;
  totalDurationMs: number;
  itemsCount: number;
  status: MenuImportStatus;
  errorMessage: string | null;
  extractedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reuseTelemetry: ImportReuseTelemetry | null;
}

export function getMenuImport(
  id: number,
  signal?: AbortSignal,
): Promise<MenuImportDetail> {
  return apiCall<MenuImportDetail>(`/api/admin/menu-imports/${id}`, { signal });
}

// ---- menu_imports source bytes -------------------------------------------
//
// /:id/source streams the original file with auth required. <iframe> / <img>
// can't attach a Bearer header to a URL load, so we fetch the bytes as a
// Blob and hand back an object-URL the embed tags can consume. Callers must
// revoke the URL when they're done with it to release memory.

import { config } from '@/config';
import { refreshIfNeeded } from '@/auth/auth-service';

export interface ImportSourceObject {
  /** A `blob:` URL the caller embeds via <iframe src> or <img src>. */
  url: string;
  /** Content-Type the server reported, useful for picking <iframe> vs <img>. */
  contentType: string;
  /** Call this when the embed is unmounting to release memory. */
  revoke: () => void;
}

// ---- menu_imports edit ---------------------------------------------------

export interface EditMenuImportBody {
  sections: ExtractedMenuSection[];
}

export interface EditMenuImportResponse {
  ok: true;
  items_count: number;
}

export function editMenuImport(
  id: number,
  body: EditMenuImportBody,
  signal?: AbortSignal,
): Promise<EditMenuImportResponse> {
  return apiCall<EditMenuImportResponse>(`/api/admin/menu-imports/${id}/edit`, {
    method: 'POST',
    body,
    signal,
  });
}

// ---- menu_imports approve + reject ---------------------------------------

export interface ApproveMenuImportResponse {
  approvedItemsCount: number;
  replacedItemsCount: number;
}

export function approveMenuImport(
  id: number,
  signal?: AbortSignal,
): Promise<ApproveMenuImportResponse> {
  return apiCall<ApproveMenuImportResponse>(
    `/api/admin/menu-imports/${id}/approve`,
    { method: 'POST', body: {}, signal },
  );
}

export interface RejectMenuImportBody {
  reason?: string;
}

export function rejectMenuImport(
  id: number,
  body: RejectMenuImportBody,
  signal?: AbortSignal,
): Promise<{ ok: true }> {
  return apiCall<{ ok: true }>(`/api/admin/menu-imports/${id}/reject`, {
    method: 'POST',
    body,
    signal,
  });
}

export async function fetchMenuImportSource(
  id: number,
  signal?: AbortSignal,
): Promise<ImportSourceObject> {
  const token = await refreshIfNeeded();
  if (!token) throw new Error('not_authenticated');
  // Tell the fetch to NOT follow the http_url redirect — it's served by
  // a different origin (the restaurant's site) and we want to surface the
  // URL to the caller, not silently rewrite it into a blob.
  const response = await fetch(`${config.apiUrl}/api/admin/menu-imports/${id}/source`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
    redirect: 'manual',
  });

  // 302 → http_url source. Fetch with `redirect: 'manual'` returns an
  // opaqueredirect response we can't read; rely on the caller to handle
  // http_url sources by inspecting MenuImportDetail.sourceKind first
  // (cheaper than chasing the redirect target through CORS).
  if (response.type === 'opaqueredirect') {
    throw new Error('source_is_external_url');
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`fetch_source_failed: ${response.status} ${text.slice(0, 80)}`);
  }
  const contentType = response.headers.get('content-type') ?? 'application/octet-stream';
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return {
    url,
    contentType,
    revoke: () => URL.revokeObjectURL(url),
  };
}

// ---- recipes (curation) --------------------------------------------------

export type RecipeConfidence = 'draft' | 'verified';
export type RecipeSource =
  | 'crea' | 'llm' | 'recipe_site' | 'recipe_import' | 'founder' | 'import_shell' | 'seed' | 'ingredient';
export type ReviewStatus = 'pending' | 'auto_passed' | 'flagged' | 'human_reviewed';

export interface RecipeSummary {
  id: number;
  dishFamilyId: number;
  dishFamilyNameEn: string;
  dishFamilyNameRo: string;
  variantName: string;
  recipeCategory: string | null;
  recipeYieldGrams: number;
  recipeYieldServings: number;
  confidence: RecipeConfidence;
  ingredientsCount: number;
  updatedAt: string;
  source: RecipeSource;
  reviewStatus: ReviewStatus;
  openAnomalies: number;
}

export interface RecipeAnomaly {
  id: number;
  ingredientId: number | null;
  kind: string;
  severity: 'info' | 'warn' | 'block';
  detectedAt: string;
  detectedBy: string;
  message: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

export interface RecipeIngredient {
  ingredientId: number;
  grams: number;
  role: string | null;
  ingredientNameEn: string;
  ingredientNameRo: string;
  ingredientCategory: string;
}

export interface RecipeDetail extends RecipeSummary {
  familyAttributes: {
    proteins: Array<{ code: string; confidence: number }>;
    cookingMethod: string | null;
    methodConfidence: number | null;
    baseForm: string | null;
    dishConcept: string | null;
    attrModel: string | null;
    attrVersion: number | null;
  };
  description: string | null;
  sourceUrl: string | null;
  notes: string | null;
  imageUrl: string | null;
  datePublished: string | null;
  createdAt: string;
  ingredients: RecipeIngredient[];
  dietTypes: Array<{ id: number; code: string; nameEn: string }>;
  generationModel: string | null;
  generatedAt: string | null;
  lastReviewedAt: string | null;
  lastReviewedBy: string | null;
  anomalies: RecipeAnomaly[];
}

export interface ListRecipesParams {
  dish_family_id?: number;
  recipe_category?: string;
  confidence?: RecipeConfidence;
  review_status?: ReviewStatus;
  source?: RecipeSource;
  limit?: number;
  offset?: number;
}

export interface ListRecipesResponse {
  rows: RecipeSummary[];
  total: number;
}

export function listRecipes(
  params: ListRecipesParams,
  signal?: AbortSignal,
): Promise<ListRecipesResponse> {
  const search = new URLSearchParams();
  if (params.dish_family_id !== undefined) {
    search.set('dish_family_id', String(params.dish_family_id));
  }
  if (params.recipe_category) search.set('recipe_category', params.recipe_category);
  if (params.confidence) search.set('confidence', params.confidence);
  if (params.review_status) search.set('review_status', params.review_status);
  if (params.source) search.set('source', params.source);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const qs = search.toString();
  const path = qs ? `/api/admin/recipes?${qs}` : '/api/admin/recipes';
  return apiCall<ListRecipesResponse>(path, { signal });
}

export function getRecipe(id: number, signal?: AbortSignal): Promise<RecipeDetail> {
  return apiCall<RecipeDetail>(`/api/admin/recipes/${id}`, { signal });
}

export function deleteRecipe(
  id: number,
): Promise<{ deleted: boolean; family_deleted: boolean }> {
  return apiCall<{ deleted: boolean; family_deleted: boolean }>(
    `/api/admin/recipes/${id}`,
    { method: 'DELETE' },
  );
}

export function resolveRecipeAnomaly(
  recipeId: number,
  anomalyId: number,
  note?: string,
): Promise<{ resolved: boolean }> {
  return apiCall<{ resolved: boolean }>(
    `/api/admin/recipes/${recipeId}/anomalies/${anomalyId}/resolve`,
    { method: 'POST', body: JSON.stringify(note ? { note } : {}) },
  );
}

export function approveRecipe(id: number): Promise<{ approved: boolean }> {
  return apiCall<{ approved: boolean }>(`/api/admin/recipes/${id}/approve`, {
    method: 'POST',
    body: '{}',
  });
}

export function bulkPromoteRecipes(source?: RecipeSource): Promise<{ promoted: number }> {
  return apiCall<{ promoted: number }>('/api/admin/recipes/bulk-promote', {
    method: 'POST',
    body: JSON.stringify(source ? { source } : {}),
  });
}

export interface CreateRecipeBody {
  dish_family_id: number;
  variant_name: string;
  recipe_yield_grams: number;
  recipe_yield_servings?: number;
  recipe_category?: string | null;
  description?: string | null;
  source_url?: string | null;
  notes?: string | null;
  ingredients?: Array<{ ingredient_id: number; grams: number; role?: string | null }>;
}

export function createRecipe(
  body: CreateRecipeBody,
  signal?: AbortSignal,
): Promise<{ id: number }> {
  return apiCall<{ id: number }>('/api/admin/recipes', {
    method: 'POST',
    body,
    signal,
  });
}

export interface PatchRecipeBody {
  variant_name?: string;
  description?: string | null;
  recipe_category?: string | null;
  recipe_yield_grams?: number;
  recipe_yield_servings?: number;
  source_url?: string | null;
  notes?: string | null;
  confidence?: RecipeConfidence;
}

export function patchRecipe(
  id: number,
  body: PatchRecipeBody,
  signal?: AbortSignal,
): Promise<{ ok: true }> {
  return apiCall<{ ok: true }>(`/api/admin/recipes/${id}`, {
    method: 'PATCH',
    body,
    signal,
  });
}

export interface ReplaceIngredientsBody {
  ingredients: Array<{ ingredient_id: number; grams: number; role?: string | null }>;
}

export function replaceRecipeIngredients(
  id: number,
  body: ReplaceIngredientsBody,
  signal?: AbortSignal,
): Promise<{ ok: true; ingredients_count: number }> {
  return apiCall<{ ok: true; ingredients_count: number }>(
    `/api/admin/recipes/${id}/ingredients`,
    { method: 'POST', body, signal },
  );
}

export interface DishFamilySummary {
  id: number;
  nameEn: string;
  nameRo: string;
  recipeCuisine: string;
}

export function searchDishFamilies(
  q: string,
  limit = 20,
  signal?: AbortSignal,
): Promise<{ matches: DishFamilySummary[] }> {
  const search = new URLSearchParams({ q, limit: String(limit) });
  return apiCall<{ matches: DishFamilySummary[] }>(
    `/api/admin/dish-families?${search}`,
    { signal },
  );
}

export interface CreateDishFamilyBody {
  name_en: string;
  name_ro: string;
  recipe_cuisine: string;
  description?: string | null;
  notes?: string | null;
}

export function createDishFamily(
  body: CreateDishFamilyBody,
  signal?: AbortSignal,
): Promise<DishFamilySummary> {
  return apiCall<DishFamilySummary>('/api/admin/dish-families', {
    method: 'POST',
    body,
    signal,
  });
}

// ---- ingredients (catalog) -----------------------------------------------
// The catalog endpoint isn't admin-gated but the recipe-detail page needs
// ingredient autocomplete and the existing endpoint is the canonical one.

export interface IngredientLookupMatch {
  ingredient_id: number;
  ingredient_name_en: string;
  ingredient_name_ro: string;
  confidence: number;
  tier: string;
  matched_alias: string;
  matched_alias_language: string;
}

export function lookupIngredient(
  name: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<{ matches: IngredientLookupMatch[] }> {
  return apiCall<{ matches: IngredientLookupMatch[] }>(
    '/api/catalog/lookup-ingredient',
    { method: 'POST', body: { name, limit }, signal },
  );
}

// ---- menu_items category + subcategory cleanup -------------------------
// MENU_QUALITY_PLAN Phase A4 (coarse) + Phase H4 (subcategory). Same
// endpoints handle both — the `mode` query param decides which column
// the "unknown" filter looks at, and the POST body accepts category,
// subcategory, or both.

export type ItemCategory = 'food' | 'beverage' | 'dessert' | 'side';
export type ItemSubcategory =
  | 'soup'
  | 'main'
  | 'salad'
  | 'side'
  | 'dessert'
  | 'coffee_tea'
  | 'cold_drink'
  | 'alcoholic'
  | 'appetizer';

export type UnknownMode = 'category' | 'subcategory';

export interface UnknownItemRow {
  id: number;
  title: string;
  section: string | null;
  item_category: string;
  item_subcategory: string;
  restaurant_id: number;
  restaurant_slug: string;
  restaurant_name: string;
}

export interface UnknownItemGroup {
  restaurant_id: number;
  restaurant_slug: string;
  restaurant_name: string;
  section: string | null;
  n: number;
}

export interface ListUnknownItemsResponse {
  rows: UnknownItemRow[];
  total: number;
  groups: UnknownItemGroup[];
  mode: UnknownMode;
}

export interface ListUnknownItemsParams {
  mode?: UnknownMode;
  section_filter?: string;
  restaurant_id?: number;
  limit?: number;
  offset?: number;
}

export function listUnknownMenuItems(
  params: ListUnknownItemsParams,
  signal?: AbortSignal,
): Promise<ListUnknownItemsResponse> {
  const search = new URLSearchParams();
  if (params.mode) search.set('mode', params.mode);
  if (params.section_filter) search.set('section_filter', params.section_filter);
  if (params.restaurant_id !== undefined) {
    search.set('restaurant_id', String(params.restaurant_id));
  }
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const qs = search.toString();
  const path = qs
    ? `/api/admin/menu-items/unknown?${qs}`
    : '/api/admin/menu-items/unknown';
  return apiCall<ListUnknownItemsResponse>(path, { signal });
}

export interface SetCategoryBody {
  ids: number[];
  category?: ItemCategory;
  subcategory?: ItemSubcategory;
}

export function setMenuItemCategory(
  body: SetCategoryBody,
  signal?: AbortSignal,
): Promise<{ ok: true; updated: number }> {
  return apiCall<{ ok: true; updated: number }>(
    '/api/admin/menu-items/category',
    { method: 'POST', body, signal },
  );
}

/** Hard-delete a menu item. Used to prune meta-rows the LLM scanner
 *  mis-ingested as dishes (combo offers, section headers etc.).
 *  Backend audit-logs the delete + the row is recoverable from the
 *  original menu_imports approval if a delete was a mistake. */
export function deleteMenuItem(
  id: number,
  signal?: AbortSignal,
): Promise<{ ok: true; deleted: number }> {
  return apiCall<{ ok: true; deleted: number }>(
    `/api/admin/menu-items/${id}`,
    { method: 'DELETE', signal },
  );
}

// Simple list of restaurants for admin pickers. Backed by the public
// /api/restaurants endpoint — admin doesn't need anything fancier.
export interface AdminRestaurantSummary {
  id: number;
  slug: string;
  name: string;
  total_items: number;
}

export interface ListAdminRestaurantsResponse {
  restaurants: AdminRestaurantSummary[];
}

export function listAdminRestaurants(
  signal?: AbortSignal,
): Promise<ListAdminRestaurantsResponse> {
  return apiCall<ListAdminRestaurantsResponse>('/api/restaurants', { signal });
}

// ── Edit any menu item — per-restaurant edit page ────────────────────
// Distinct from the bulk "set category" endpoint above: this is a
// per-item PATCH that supports title + section in addition to
// category/subcategory. Backs the EditMenuItemsPage (2026-05-24)
// which lets the admin fix the broader class of LLM mis-extraction
// bugs the audit script found.

export interface EditableMenuItem {
  id: number;
  title: string;
  section: string | null;
  item_category: string;
  item_subcategory: string;
}

export interface ListEditableMenuItemsResponse {
  restaurant: { id: number; name: string; slug: string };
  items: EditableMenuItem[];
}

export function listEditableMenuItems(
  slug: string,
  signal?: AbortSignal,
): Promise<ListEditableMenuItemsResponse> {
  return apiCall<ListEditableMenuItemsResponse>(
    `/api/admin/restaurants/${encodeURIComponent(slug)}/menu-items`,
    { signal },
  );
}

export interface PatchMenuItemBody {
  title?: string;
  section?: string | null;
  item_category?: ItemCategory | 'unknown';
  item_subcategory?: ItemSubcategory | 'unknown';
}

export function patchMenuItem(
  id: number,
  body: PatchMenuItemBody,
  signal?: AbortSignal,
): Promise<{ ok: true; updated: number }> {
  return apiCall<{ ok: true; updated: number }>(
    `/api/admin/menu-items/${id}`,
    { method: 'PATCH', body, signal },
  );
}

// ---- dish identity: founder labels (plan L5/L6) ---------------------------

// NOT_A_DISH_A / _B / _BOTH: on a pair, that side (or both) is not a dish (an extra,
// a topping, a sauce); stored as not-a-dish labels on the line(s).
export type HumanLabelValue = 'SAME' | 'DIFFERENT' | 'NOT_A_DISH' | 'DISH' | 'NOT_A_DISH_A' | 'NOT_A_DISH_B' | 'NOT_A_DISH_BOTH';

export interface HumanLabelLine {
  title: string;
  description: string | null;
  section: string | null;
  ingredients: string[];
  declaredAllergens: string[];
  restaurant: string;
}

export interface HumanLabelItem {
  item: number;
  a: HumanLabelLine;
  b: HumanLabelLine | null;
  label: HumanLabelValue | null;
}

export function getHumanLabelSet(
  round: string,
  calibrationOnly: boolean,
  signal?: AbortSignal,
): Promise<{ round: string; items: HumanLabelItem[] }> {
  const q = new URLSearchParams({ round, calibration: calibrationOnly ? '1' : '0' });
  return apiCall(`/api/admin/identity/labels?${q.toString()}`, { signal });
}

export function setHumanLabel(
  round: string,
  item: number,
  value: HumanLabelValue | null,
): Promise<{ ok: true }> {
  return apiCall('/api/admin/identity/labels', { method: 'POST', body: { round, item, value } });
}

// ---- usage statistics (Stage 1a; docs/done/ANALYTICS_IMPLEMENTATION_PLAN.md D1) ----

export interface UsageCohort {
  week_start: string;
  size: number;
  weeks: Array<{ week: number; retained: number; rate: number | null; complete: boolean }>;
}

export interface UsageReport {
  generated_at: string;
  measurement_start: string;
  actives: { dau: number; wau: number; mau: number };
  signups_by_week: Array<{ week_start: string; signups: number }>;
  platform_30d: Array<{ platform: string; users: number }>;
  cohorts: UsageCohort[];
  coverage_demand: Array<{
    restaurant_id: number;
    slug: string | null;
    name: string | null;
    market: string | null;
    area: string;
    users: number;
    user_opens: number;
    anon_opens: number;
    has_menu_now: boolean;
  }>;
  menu_share_30d: Array<{ market: string | null; opens: number; with_menu: number }>;
  menu_share_by_area_30d?: Array<{ area: string; opens: number; with_menu: number }>;
  counters_30d: Array<{ kind: string; n: number }>;
  notes: string[];
}

export function getUsageReport(signal?: AbortSignal): Promise<UsageReport> {
  return apiCall<UsageReport>('/api/admin/usage', { signal });
}
